import { dateVariables } from "@arenko-group/arenko-ui/dist/enums";
import { dateTimeRangeTypes } from "@arenko-group/arenko-ui/dist/enums";
import {
  dateToCustomFormatString,
  getBufferArrayFromValue,
} from "@arenko-group/arenko-ui/dist/helpers";
import { compareAsc } from "date-fns";
import { millisecondBufferToShareTooltip } from "@/enums/chart.enum";
//reducers
import type { YAxisRangeType } from "@/types/common/websockets.types";
import type {
  SelectedOutputObj,
  SelectedRange,
} from "@/types/data/redux.types";
import type {
  ExtendedHighchartsSeriesType,
  GroupedSeries,
  PlotData,
  Profile,
  SeriesDataArr,
  SeriesWithInfo,
  TimeRange,
} from "@/types/data/schedule.types";
import type { HighchartsTooltipPointContext } from "../components/Chart/PowerChart";

export const getSelectedRangeType = () => {
  const storedRange = localStorage.getItem(dateVariables.preselectedRangeSEUI);
  return storedRange &&
    Object.keys(dateTimeRangeTypes).includes(storedRange) &&
    storedRange !== dateTimeRangeTypes.custom
    ? storedRange
    : dateTimeRangeTypes.efaDay;
};

export const showCurrentTime = (
  currentTime: number,
  timeRange: TimeRange<number>
) =>
  compareAsc(new Date(currentTime), new Date(timeRange.from)) === 1 &&
  compareAsc(new Date(timeRange.to), new Date(currentTime)) === 1;

export const absolutesToPoints = (absolutes: Profile[] = []): SeriesDataArr[] =>
  absolutes.map((abs) => [
    new Date(abs.time).getTime(),
    convertWattToMW(abs.value),
  ]);

export const absolutesToArea = (
  absolutes: Profile[] = [],
  bases: Profile[] = []
) =>
  absolutes.map((abs, i) => [
    new Date(abs.time).getTime(),
    ...[convertWattToMW(abs.value), convertWattToMW(bases[i].value)].sort(
      (a, b) => a - b
    ),
  ]);

export const convertWattToMW = (value: number) => value / 1000000;

const getSeriesInfo = (
  series: ExtendedHighchartsSeriesType[],
  seriesIndexToShow: number[],
  selectedOutputObj: SelectedOutputObj,
  selectedRange: SelectedRange
) => {
  const seriesWithInfo: {
    value: string;
    time: string;
    schedule: ExtendedHighchartsSeriesType;
  }[] = [];
  series.forEach((s, index) => {
    let indexToShow = seriesIndexToShow[index];
    const profile: Profile[] = s?.options?.scheduleData?.profile ?? [];
    const dataLength = s.data?.length ?? 0;
    //handle tooltips for stacked schedules
    if (dataLength > 4 && dataLength !== profile.length && indexToShow !== 0) {
      if (indexToShow === dataLength - 1) indexToShow = profile.length - 1;
      else indexToShow = profile.length - 2;
    }
    const pointData = profile?.[indexToShow];
    const value = selectedOutputObj?.scale
      ? (pointData.value / selectedOutputObj?.scale).toFixed(1)
      : "";

    const tooltipTime = s.data?.[seriesIndexToShow[index]]?.x;

    const toDate = selectedRange?.to ? new Date(selectedRange?.to) : new Date();
    const fromDate = selectedRange?.from
      ? new Date(selectedRange?.from)
      : new Date();

    const isRangeCrossingDateBoundary = !(
      toDate.getFullYear() === fromDate.getFullYear() &&
      toDate.getMonth() === fromDate.getMonth() &&
      toDate.getDay() === fromDate.getDay()
    );

    const dateStringFormat =
      new Date(tooltipTime ?? 0).getMilliseconds() > 0
        ? "HH:mm:ss.SSS"
        : "HH:mm:ss";

    const time = dateToCustomFormatString(
      new Date(tooltipTime ?? 0),
      isRangeCrossingDateBoundary
        ? `dd/MM/yyyy ${dateStringFormat}`
        : dateStringFormat
    );

    seriesWithInfo.push({
      value: value,
      time: time,
      schedule: s,
    });
  });

  return seriesWithInfo;
};

export const getTooltipInfo = (
  series: Highcharts.Series,
  point: HighchartsTooltipPointContext,
  selectedOutputObj: SelectedOutputObj,
  selectedRange: SelectedRange,
  pointX?: number,
  pointY?: number
) => {
  const uniqueScheduleIds: string[] = [];
  const y = series.data[point.index]?.y;
  const x = series.data[point.index]?.x;
  const seriesIndexToShow: number[] = [];
  const filteredSeries: ExtendedHighchartsSeriesType[] = [];
  //to check for nearby points to include in the same tooltip
  const checkNearbyPointsWithSameYValue = (
    s: ExtendedHighchartsSeriesType,
    x: number,
    y: number,
    millisecondBuffer: number
  ) =>
    getBufferArrayFromValue(x, millisecondBuffer).some((item) => {
      const idx = s.data?.findIndex((pt) => pt.x === item);
      return idx !== undefined && idx !== -1 && s.data[idx]?.y === y;
    });

  //to check for nearby points to include in the same tooltip
  const checkNearbyPointsForXValue = (
    val: number,
    s: ExtendedHighchartsSeriesType,
    index: number,
    millisecondBuffer: number
  ) =>
    getBufferArrayFromValue(val, millisecondBuffer).includes(
      s.data?.[index]?.x
    );

  //get relevant series for the point that user is hovering on
  (series.chart.series as ExtendedHighchartsSeriesType[]).forEach((s) => {
    if (
      s.visible &&
      s?.options?.scheduleData &&
      !uniqueScheduleIds.includes(s?.options?.scheduleData.id) &&
      checkNearbyPointsWithSameYValue(
        s,
        x ?? 0,
        y ?? 0,
        millisecondBufferToShareTooltip
      )
    ) {
      (s.data ?? []).forEach((pt, index) => {
        if (
          pt.y === pointY &&
          checkNearbyPointsForXValue(
            pointX ?? 0,
            s,
            index,
            millisecondBufferToShareTooltip
          )
        ) {
          seriesIndexToShow.push(index);
        }
      });
      uniqueScheduleIds.push(s?.options?.scheduleData.id);
      filteredSeries.push(s);
    }
  });

  //get time and value for each point of the series
  const seriesWithInfo: SeriesWithInfo[] = getSeriesInfo(
    filteredSeries,
    seriesIndexToShow,
    selectedOutputObj,
    selectedRange
  );

  //group series with the same time and value so that they can be arranged together in the tooltip
  const groupedSeries = seriesWithInfo.reduce(
    (result: GroupedSeries[], obj: SeriesWithInfo) => {
      const { value, time } = obj;
      const existingItem = result.find(
        (item) => item.value === value && item.time === time
      );
      const schData = obj?.schedule?.options?.scheduleData;
      if (schData) {
        if (existingItem) {
          existingItem.schedules.push(schData);
        } else {
          result.push({
            value,
            time,
            schedules: [schData],
          });
        }
      }

      return result;
    },
    []
  );

  return groupedSeries;
};

export const getEmptyScheduleObject = () => {
  return {
    id: "",
    deviceId: "",
    controllerId: "",
    origin: "",
    type: "",
    output: "",
    controlPoint: "",
    createdAt: "",
    state: {
      state: "",
      updatedAt: "",
    },
    lock: {
      locked: false,
      origin: "",
      updatedAt: "",
    },
    profile: [
      {
        time: "",
        value: 0,
      },
    ],
  };
};

/**
 * Filters out invisible schedules when zoomed in and repeated ids.
 * @param scheduleData
 * @param isZoomed
 * @param zoomRange
 * @returns
 */
export const getFilteredData = (
  scheduleData: PlotData[],
  isZoomed: boolean,
  zoomRange: YAxisRangeType
): PlotData[] => {
  const { min, max } = zoomRange;
  const uniqueIds = new Set<string>();

  return scheduleData.filter((data: PlotData) => {
    if (uniqueIds.has(data.id)) {
      return false;
    }
    uniqueIds.add(data.id);

    if (!isZoomed) {
      return true;
    }

    const { from, to } = data.timeRange;
    const withinRange = from > min && to < max;
    const endingWithinRange = min > from && min < to;
    const startingWithinRange = max > from && max < to;

    return endingWithinRange || withinRange || startingWithinRange;
  });
};
